import React, { useEffect, useState, useRef, useCallback } from "react";
import { ImageOverlay, useMap } from "react-leaflet";
import { fromArrayBuffer } from "geotiff";
import L from "leaflet";

// Add a new prop: clipPercent
const GeoTIFFLayer = ({ url, opacity = 1.0, onClick, onRegister, onUnregister, layerType, district, year, clipPercent, clipDirection = 'right', pane, onBoundsLoad }) => {
    const [imageDataUrl, setImageDataUrl] = useState(null);
    const [bounds, setBounds] = useState(null);
    const colorMap = useRef({});
    const geoTiffImage = useRef(null); // Store the loaded geotiff image object
    const rastersRef = useRef(null);
    const map = useMap();
    const layerRef = useRef(null);
    const clipPercentRef = useRef(clipPercent);

    // Update the ref whenever clipPercent changes
    useEffect(() => {
        clipPercentRef.current = clipPercent;
    }, [clipPercent]);

    // Load color map JSON
    useEffect(() => {
        const loadColorMap = async () => {
            try {
                const res = await fetch("/forest_colors.json");
                const json = await res.json();
                const map = {};
                json.forEach(item => {
                    if (
                        item.MapValue !== undefined &&
                        item.ColorCode &&
                        item.ColorCode.length === 6
                    ) {
                        map[item.MapValue] = item.ColorCode;
                    }
                });
                colorMap.current = map;
            } catch (err) {
                console.error("Failed to load colormap.json:", err);
            }
        };

        loadColorMap();
    }, []);

    // Load GeoTIFF and render to canvas
    useEffect(() => {
        let isMounted = true;

        const loadTiff = async () => {
            try {
                const response = await fetch(url);
                const arrayBuffer = await response.arrayBuffer();
                const tiff = await fromArrayBuffer(arrayBuffer);
                const image = await tiff.getImage();
                geoTiffImage.current = image; // Store the image object

                const rasters = await image.readRasters();
                rastersRef.current = rasters;
                const bbox = image.getBoundingBox();

                const latLngBounds = L.latLngBounds(
                    [bbox[1], bbox[0]],
                    [bbox[3], bbox[2]]
                );

                // Send bounds back to parent component
                if (isMounted && onBoundsLoad) {
                    onBoundsLoad(latLngBounds, district, year);
                }

                const canvas = document.createElement("canvas");
                const width = image.getWidth();
                const height = image.getHeight();
                canvas.width = width;
                canvas.height = height;

                const ctx = canvas.getContext("2d");
                const imgData = ctx.createImageData(width, height);

                for (let i = 0; i < imgData.data.length; i += 4) {
                    const pixelIndex = i / 4;
                    const val = rasters[0][pixelIndex];
                
                    // Transparent if NoData
                    if (val === 0 || val === undefined || isNaN(val)) {
                        imgData.data[i] = 0;
                        imgData.data[i + 1] = 0;
                        imgData.data[i + 2] = 0;
                        imgData.data[i + 3] = 0; // fully transparent
                    } else if (val === 1) {
                        // If the pixel value is 1 (for mangrove coverage)
                        // Apply the specific color for mangrove (e.g., green)
                        imgData.data[i] = 0;       // Red (set to 0 for green)
                        imgData.data[i + 1] = 128; // Green (set to 128 for green)
                        imgData.data[i + 2] = 0;   // Blue (set to 0 for green)
                        imgData.data[i + 3] = 255; // Fully opaque
                    } else {
                        // Cropland layer
                        if (url.includes('/cropland/')) {
                            // Yellow for cropland (RGB: 255, 255, 0)
                            imgData.data[i] = 246;     // Red = 246
                            imgData.data[i + 1] = 190; // Green = 190
                            imgData.data[i + 2] = 0;   // Blue = 0
                            imgData.data[i + 3] = 255; // Slightly transparent
                        } 

                        else {
                            // If a colormap exists for this value, use it (for other coverage types)
                            const hex = colorMap.current[val];
                    
                            if (hex) {
                                const r = parseInt(hex.substring(0, 2), 16);
                                const g = parseInt(hex.substring(2, 4), 16);
                                const b = parseInt(hex.substring(4, 6), 16);
                    
                                imgData.data[i] = r;
                                imgData.data[i + 1] = g;
                                imgData.data[i + 2] = b;
                                imgData.data[i + 3] = 255; // opaque
                            } else {
                                // Default color for non-zero values (other coverage types)
                                imgData.data[i] = 255;     // red
                                imgData.data[i + 1] = 255; // green
                                imgData.data[i + 2] = 255; // blue
                                imgData.data[i + 3] = 255; // opaque 
                            }
                        }
                    }
                }                

                ctx.putImageData(imgData, 0, 0);

                if (isMounted) {
                    setImageDataUrl(canvas.toDataURL());
                    setBounds(latLngBounds);
                }

                // Register a value accessor for map-level hit-test
                if (isMounted && onRegister && district && layerType === 'mangrove') {
                    const getValueAt = (latlng) => {
                        if (!geoTiffImage.current || !rastersRef.current) return 0;
                        const img = geoTiffImage.current;
                        const width = img.getWidth();
                        const height = img.getHeight();
                        const [minX, minY, maxX, maxY] = img.getBoundingBox();
                        const { lat, lng } = latlng;
                        const x = Math.floor(((lng - minX) / (maxX - minX)) * width);
                        const y = Math.floor(((maxY - lat) / (maxY - minY)) * height);

                        if (x < 0 || x >= width || y < 0 || y >= height) return 0;
                        const idx = y * width + x;
                        return rastersRef.current[0][idx] ?? 0;
                    };
                    onRegister(district, getValueAt, year);
                }
            } catch (err) {
                console.error("Failed to load and render GeoTIFF:", err);
            }
        };

        loadTiff();

        return () => {
            isMounted = false;
            if (onUnregister && district && layerType === 'mangrove') {
                onUnregister(district);
            }
        };
    }, [url, district, layerType, year, onRegister, onUnregister, onBoundsLoad]);

    const getOverlayElement = useCallback(() => {
        if (!layerRef.current) return null;
        if (typeof layerRef.current.getElement === 'function') {
            return layerRef.current.getElement();
        }
        return layerRef.current._image ?? layerRef.current._element ?? null;
    }, []);

    const applyClip = useCallback(() => {
        const currentClipPercent = clipPercentRef.current;
        if (currentClipPercent === undefined) return;
        
        const element = getOverlayElement();
        if (!element) return;
        
        const inset = clipDirection === 'left'
            ? `inset(0 ${currentClipPercent}% 0 0)`
            : `inset(0 0 0 ${currentClipPercent}%)`;
        
        element.style.clipPath = inset;
        element.style.webkitClipPath = inset;
        
        console.log(`Clip applied: ${clipDirection} at ${currentClipPercent}%`);
    }, [clipDirection, getOverlayElement]); // clipPercent removed from deps

    // // Add clipping if specified
    // useEffect(() => {
    //     if (clipPercent !== undefined && map) {
    //         const mapContainer = map.getContainer();
    //         const layers = mapContainer.querySelectorAll('.leaflet-image-layer');
            
    //         // Apply clip to the last added layer (the one we just created)
    //         const ourLayer = layers[layers.length - 1];
    //         if (ourLayer) {
    //             ourLayer.style.clipPath = `inset(0 0 0 ${clipPercent}%)`;
    //             ourLayer.style.webkitClipPath = `inset(0 0 0 ${clipPercent}%)`;
    //         }
    //     }
    // }, [clipPercent, map, url]);

    // // Update clip when slider moves
    // useEffect(() => {
    //     if (clipPercent === undefined || !layerRef.current) return;
    
    //     // Need to wait until the Leaflet component is properly mounted and has created DOM elements
    //     setTimeout(() => {
    //         try {
    //             // Get the actual DOM element created by Leaflet
    //             const element = layerRef.current._element;
                
    //             if (element) {
    //                 console.log(`Applying clip-path: inset(0 0 0 ${clipPercent}%)`);
    //                 element.style.clipPath = `inset(0 0 0 ${clipPercent}%)`;
    //                 element.style.webkitClipPath = `inset(0 0 0 ${clipPercent}%)`;
    //             } else {
    //                 console.log('Leaflet element not found');
    //             }
    //         } catch (err) {
    //             console.error('Error applying clip-path:', err);
    //         }
    //     }, 50); // Short delay to ensure Leaflet has created the DOM elements
    // }, [clipPercent, map]);

    const handleLayerClick = async (e) => {
        console.log('ImageOverlay clicked at', e.latlng, 'for URL:', url)

        // Stop the event from propagating to the map, which might interpret it as a pan
        L.DomEvent.stopPropagation(e);

        if (!geoTiffImage.current || !onClick){
            console.warn('GeoTIFF image not ready yet');
            return;
        } 

        try {
            const image = geoTiffImage.current;
            const width = image.getWidth();
            const height = image.getHeight();
            const rasters = await image.readRasters();

            // Use bounding box to convert map coords to pixel indices
            const [minX, minY, maxX, maxY] = image.getBoundingBox(); // [west, south, east, north]
            const { lat, lng } = e.latlng;

            // Convert lng/lat to pixel (x,y) using linear transform
            const x = Math.floor(((lng - minX) / (maxX - minX)) * width);
            const y = Math.floor(((maxY - lat) / (maxY - minY)) * height);

            console.log('Computed pixel coords via bbox', { x, y, width, height, bbox: [minX, minY, maxX, maxY] });

            if (x >= 0 && x < width && y >= 0 && y < height) {
                const pixelIndex = y * width + x;
                const value = rasters[0][pixelIndex];
                console.log(`GeoTIFFLayer: Clicked pixel value is ${value}`);
                onClick(e.latlng, value);
            } else {
                console.log('Click outside raster bounds (bbox method)');
            }
        } catch (err) {
            console.error('Error reading raster data on click:', err);
        }
    };

    useEffect(() => {
        if (clipPercent === undefined) return;
        const timer = setTimeout(applyClip, 0);
        return () => clearTimeout(timer);
    }, [clipPercent, clipDirection, imageDataUrl, applyClip]);

    // This ensures the clip is applied immediately when the slider moves
    useEffect(() => {
        if (clipPercent === undefined) return;
        
        // For immediate response to slider movement
        applyClip();
        
        // Also apply when the image renders
        const timer = setTimeout(() => {
            const element = getOverlayElement();
            if (element) {
                console.log(`Applied clip: ${clipDirection === 'left' ? 'LEFT' : 'RIGHT'} at ${clipPercent}%`);
            }
        }, 10);
        
        return () => clearTimeout(timer);
    }, [clipPercent, clipDirection, imageDataUrl, applyClip]);

    if (!imageDataUrl || !bounds) return null;

    return (
        <ImageOverlay
            ref={layerRef}
            url={imageDataUrl}
            bounds={bounds}
            opacity={opacity}
            eventHandlers={{
                ...(onClick ? { click: handleLayerClick } : {}),
                load: applyClip
            }}
            {...(pane ? { pane } : {})}
            interactive={true}
        />
    );
};

export default GeoTIFFLayer;